import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaLeaf, FaCalendarCheck, FaClock, FaUserMd, FaAppleAlt, FaHeartbeat, FaTimes } from 'react-icons/fa';
import AppDownloadSection from '../../components/AppDownloadSection/AppDownloadSection';
import dietitianDrVijayImage from '../../assets/images/dietitian-dr-vijay.png';
import physiotherapistImage from '../../assets/images/physiotherapist-dr-sai-vasavi.png';
import { sanitizePhoneDigitsInput, validatePhoneDigitsValue } from '../../utils/phone';
import { submitContactForm } from '../../api/webPublicApi';
import { ApiError } from '../../api/client';
import './Contact.css';

const Contact = () => {
  const [form, setForm] = useState({ firstName: '', lastName: '', mobile: '', email: '', message: '' });
  const [status, setStatus] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [loading, setLoading] = useState(false);

  const [dietModalOpen, setDietModalOpen] = useState(false);
  const [dietForm, setDietForm] = useState({ name: '', email: '', mobile: '', date: '', time: '', concern: '' });
  const [dietStatus, setDietStatus] = useState('');
  const [dietStatusMessage, setDietStatusMessage] = useState('');
  const [dietLoading, setDietLoading] = useState(false);
  const [consultationType, setConsultationType] = useState('dietitian');

  const getTomorrowDate = () => {
    // Use UTC to match backend comparison
    const today = new Date();
    const tomorrow = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() + 1, 0, 0, 0, 0));
    return tomorrow.toISOString().split('T')[0];
  };

  const isPhysiotherapist = consultationType === 'physiotherapist';
  const consultationLabel = isPhysiotherapist ? 'Physiotherapist' : 'Dietitian';
  const consultationTypeValue = isPhysiotherapist ? 'physiotherapist-consultation' : 'dietitian-consultation';
  const consultationFeeAmount = isPhysiotherapist ? '₹500/-' : '₹550/-';
  const consultationDoctorName = isPhysiotherapist ? 'Dr.Sai vasavi (PT)' : 'Mr Goutham Kumar';

  const getApiErrorMessage = (err, fallback) => {
    if (err instanceof ApiError) {
      const details = err.details;
      if (Array.isArray(details?.errors) && details.errors.length > 0) {
        const hasRequiredError = details.errors.some((item) => /required/i.test(item.msg || ''));
        return hasRequiredError ? 'Fill all mandatory fields' : (details.errors[0].msg || err.message || fallback);
      }
      return err.message || fallback;
    }
    if (Array.isArray(err?.errors) && err.errors.length > 0) {
      const hasRequiredError = err.errors.some((item) => /required/i.test(item.msg || ''));
      return hasRequiredError ? 'Fill all mandatory fields' : (err.errors[0].msg || fallback);
    }
    return err?.error || err?.message || fallback;
  };

  // Lock body scroll when modal is open
  useEffect(() => {
    if (dietModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [dietModalOpen]);

  const handleDietChange = (e) => {
    const { name, value } = e.target;
    if (name === 'mobile') {
      setDietForm({ ...dietForm, mobile: sanitizePhoneDigitsInput(value, 15) });
      return;
    }
    setDietForm({ ...dietForm, [name]: value });
  };

  const handleDietSubmit = async (e) => {
    e.preventDefault();
    setDietStatus('');
    setDietStatusMessage('');

    if (!dietForm.name.trim() || !dietForm.email.trim() || !dietForm.mobile.trim() || !dietForm.date || !dietForm.time) {
      setDietStatus('error');
      setDietStatusMessage('Fill all mandatory fields');
      return;
    }

    const dietPhoneCheck = validatePhoneDigitsValue(dietForm.mobile);
    if (!dietPhoneCheck.ok) {
      setDietStatus('error');
      setDietStatusMessage(dietPhoneCheck.message);
      return;
    }

    if (dietForm.date < getTomorrowDate()) {
      setDietStatus('error');
      setDietStatusMessage('Please choose a future consultation date.');
      return;
    }

    setDietLoading(true);
    try {
      const data = await submitContactForm({ ...dietForm, type: consultationTypeValue });
      setDietStatus('success');
      setDietStatusMessage(data.message || "Booking request received! We'll confirm your slot within 24 hours.");
      setDietForm({ name: '', email: '', mobile: '', date: '', time: '', concern: '' });
    } catch (err) {
      setDietStatus('error');
      setDietStatusMessage(getApiErrorMessage(err, 'Something went wrong. Please try again.'));
    } finally {
      setDietLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'mobile') {
      setForm({ ...form, mobile: sanitizePhoneDigitsInput(value, 15) });
      return;
    }
    setForm({ ...form, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('');
    setStatusMessage('');

    if (!form.firstName.trim() || !form.lastName.trim() || !form.mobile.trim() || !form.email.trim()) {
      setStatus('error');
      setStatusMessage('Fill all mandatory fields');
      return;
    }

    const phoneCheck = validatePhoneDigitsValue(form.mobile);
    if (!phoneCheck.ok) {
      setStatus('error');
      setStatusMessage(phoneCheck.message);
      return;
    }

    setLoading(true);
    try {
      const data = await submitContactForm(form);
      setStatus('success');
      setStatusMessage(data.message || 'Your request has been received.');
      setForm({ firstName: '', lastName: '', mobile: '', email: '', message: '' });
    } catch (err) {
      setStatus('error');
      setStatusMessage(getApiErrorMessage(err, 'Something went wrong. Please try again or email us directly.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="contact-page">
      {/* Page Header */}
      <div className="page-header">
        <div className="page-header-content">
          <div className="container">
            <h1>CONTACT</h1>
            <div className="breadcrumb">
              <Link to="/">Home</Link>
              <span>/</span>
              <span>Contact</span>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Section */}
      <section className="contact-section" id="contactform">
        <div className="container">
          <div className="contact-section__grid">
            {/* Form */}
            <div className="contact-form-wrap">
              <span className="section-tag">Get In Touch</span>
              <h2 className="section-title">WE'RE HERE TO <span>HELP YOU</span></h2>
              <p className="contact-intro">We're here to help. Send us a message and our team will get back to you shortly to discuss how we can support you.</p>
              <p className="contact-note">We just need a couple of hours! No more than 2 working days since receiving your issue ticket.</p>

              {status === 'success' && (
                <div className="contact-alert contact-alert--success">
                  ✓ {statusMessage || 'Your message has been sent! We\'ll get back to you shortly.'}
                </div>
              )}
              {status === 'error' && (
                <div className="contact-alert contact-alert--error">
                  ✗ {statusMessage || 'Something went wrong. Please try again or email us directly.'}
                </div>
              )}

              <form className="contact-form" onSubmit={handleSubmit} noValidate>
                <div className="contact-form__row">
                  <div className="form-group">
                    <label htmlFor="firstName">First Name *</label>
                    <input
                      type="text"
                      id="firstName"
                      name="firstName"
                      value={form.firstName}
                      onChange={handleChange}
                      required
                      placeholder="Your first name"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="lastName">Last Name *</label>
                    <input
                      type="text"
                      id="lastName"
                      name="lastName"
                      value={form.lastName}
                      onChange={handleChange}
                      required
                      placeholder="Your last name"
                    />
                  </div>
                </div>
                <div className="contact-form__row">
                  <div className="form-group">
                    <label htmlFor="mobile">Mobile Number *</label>
                    <input
                      type="tel"
                      id="mobile"
                      name="mobile"
                      inputMode="numeric"
                      value={form.mobile}
                      onChange={handleChange}
                      required
                      placeholder="10-digit or with country code"
                      maxLength={15}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">Email *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      required
                      placeholder="your@email.com"
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="message">Message</label>
                  <textarea
                    id="message"
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    rows={5}
                    placeholder="Tell us how we can help you..."
                  />
                </div>
                <button type="submit" className="btn-primary contact-form__submit" disabled={loading}>
                  {loading ? 'SENDING...' : 'SUBMIT FORM'}
                </button>
              </form>
            </div>

            {/* Info */}
            <div className="contact-info">
              <div className="contact-info__card">
                <h4>REGISTERED ADDRESS</h4>
                <address>
                  9-22/B/20, BODUPPAL,<br />
                  GHATKESAR,<br />
                  HYDERABAD – 500092
                </address>
              </div>
              <div className="contact-info__card">
                <h4>OFFICE ADDRESS</h4>
                <address>
                  PLOT NO: HIG – 137,<br />
                  MAYURI NAGAR, MIYAPUR,<br />
                  HYDERABAD – 500049
                </address>
              </div>
              <div className="contact-info__card">
                <h4>PHONE</h4>
                <a href="tel:+918143231857">+91 8143231857</a>
              </div>
              <div className="contact-info__card">
                <h4>EMAIL</h4>
                <a href="mailto:customercare@digirasa.com">CUSTOMERCARE@DIGIRASA.COM</a>
              </div>
              <div className="contact-info__map">
                <iframe
                  title="Digirasa Location"
                  src="https://www.google.com/maps?q=DigiRasa,%20Miyapur,%20Hyderabad&z=17&output=embed"
                  width="100%"
                  height="250"
                  style={{ border: 0, borderRadius: 'var(--radius)' }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Dietitian / Physiotherapist banners */}
      <div className="dietitian-banner">
        <div className="container dietitian-banner__inner">
          <div className="dietitian-banner__list">
            <div className="dietitian-banner__item dietitian-banner__item--physio">
              <div className="dietitian-banner__text">
                <FaHeartbeat className="dietitian-banner__icon dietitian-banner__icon--physio" />
                <div>
                  <strong>Get Expert Movement &amp; Pain Relief Support</strong>
                  <span>Book a one-on-one consultation with our certified physiotherapist for recovery and mobility.</span>
                </div>
              </div>
              <button
                className="dietitian-banner__btn dietitian-banner__btn--physio"
                onClick={() => {
                  setConsultationType('physiotherapist');
                  setDietStatus('');
                  setDietStatusMessage('');
                  setDietModalOpen(true);
                }}
              >
                <FaHeartbeat /> Book a Physiotherapist Consultation
              </button>
            </div>

            <div className="dietitian-banner__item dietitian-banner__item--dietitian">
              <div className="dietitian-banner__text">
                <FaUserMd className="dietitian-banner__icon" />
                <div>
                  <strong>Get Expert Nutrition Advice</strong>
                  <span>Book a one-on-one consultation with our certified dietitian — personalised just for you.</span>
                </div>
              </div>
              <button
                className="dietitian-banner__btn"
                onClick={() => {
                  setConsultationType('dietitian');
                  setDietStatus('');
                  setDietStatusMessage('');
                  setDietModalOpen(true);
                }}
              >
                <FaCalendarCheck /> Book a Dietitian Consultation
              </button>
            </div>
          </div>
        </div>
      </div>

      <AppDownloadSection />

      {/* Dietitian Modal */}
      {dietModalOpen && (
        <div className="dietitian-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) setDietModalOpen(false); }}>
          <div className={`dietitian-modal${isPhysiotherapist ? ' dietitian-modal--physio' : ''}`}>
            <button className="dietitian-modal__close" onClick={() => setDietModalOpen(false)} aria-label="Close">
              <FaTimes />
            </button>

            <div className="dietitian-modal__grid">
              {/* Left Info Panel */}
              <div className="dietitian-info">
                <div className="dietitian-info__header">
                  {isPhysiotherapist && (
                    <div className="dietitian-info__photo-wrap">
                      <img src={physiotherapistImage} alt="Dr Sai Vasavi" className="dietitian-info__photo" />
                    </div>
                  )}
                  {!isPhysiotherapist && (
                    <div className="dietitian-info__photo-wrap">
                      <img src={dietitianDrVijayImage} alt="Mr Goutham Kumar" className="dietitian-info__photo" />
                    </div>
                  )}
                </div>
                {isPhysiotherapist ? (
                  <>
                    <p className="dietitian-info__doctor">{consultationDoctorName}</p>
                    <p className="dietitian-info__physio-line">B.P.T., M.P.T.</p>
                    <h3 className="dietitian-info__physio-line">{consultationLabel}</h3>
                    <p className="dietitian-info__doctor-credentials">
                      Antenatal &amp; Postnatal Exercise Specialist,<br />
                      Child Birth Educator( LAMAZE),<br />
                      Pelvic Health Rehab Practioner.
                    </p>
                    <p className="dietitian-info__charge"><span className="dietitian-info__charge-label">Consultation Charges</span> {consultationFeeAmount}</p>
                  </>
                ) : (
                  <>
                    <p className="dietitian-info__doctor">{consultationDoctorName}</p>
                    <p className="dietitian-info__doctor-credentials dietitian-info__doctor-credentials--role">Dietitian/Nurtionist</p>
                    <p className="dietitian-info__charge"><span className="dietitian-info__charge-label">Consultation Charges</span> {consultationFeeAmount}</p>
                  </>
                )}
                <ul className="dietitian-benefits">
                  {isPhysiotherapist ? (
                    <>
                      <li><FaHeartbeat /><span>Pain management &amp; posture correction</span></li>
                      <li><FaLeaf /><span>Mobility, flexibility &amp; strength recovery plans</span></li>
                      <li><FaCalendarCheck /><span>Flexible in-person sessions</span></li>
                      <li><FaClock /><span>30-minute &amp; 60-minute slots available</span></li>
                    </>
                  ) : (
                    <>
                      <li><FaLeaf /><span>Personalised meal &amp; diet planning</span></li>
                      <li><FaHeartbeat /><span>Weight management &amp; chronic disease support</span></li>
                      <li><FaAppleAlt /><span>Sports nutrition &amp; fitness diet plans</span></li>
                      <li><FaCalendarCheck /><span>Flexible online &amp; in-person sessions</span></li>
                      <li><FaClock /><span>30-minute &amp; 60-minute slots available</span></li>
                    </>
                  )}
                </ul>
                <div className="dietitian-info__note">
                  <span>Sessions available Mon–Sat, 9 AM – 7 PM</span>
                </div>
              </div>

              {/* Right Form */}
              <div className="dietitian-form-wrap">
                <h3>Book Your {consultationLabel} Consultation</h3>
                <p className="dietitian-form-desc">Fill in your details and preferred slot — our team will confirm your appointment within 24 hours.</p>

                {dietStatus === 'success' && (
                  <div className="contact-alert contact-alert--success">
                    ✓ {dietStatusMessage || "Booking request received! We'll confirm your slot within 24 hours."}
                  </div>
                )}
                {dietStatus === 'error' && (
                  <div className="contact-alert contact-alert--error">
                    ✗ {dietStatusMessage || 'Something went wrong. Please try again.'}
                  </div>
                )}

                <form className="dietitian-form" onSubmit={handleDietSubmit} noValidate>
                  <div className="contact-form__row">
                    <div className="form-group">
                      <label htmlFor="diet-name">Full Name *</label>
                      <input type="text" id="diet-name" name="name" value={dietForm.name} onChange={handleDietChange} required placeholder="Your full name" />
                    </div>
                    <div className="form-group">
                      <label htmlFor="diet-mobile">Mobile Number *</label>
                      <input type="tel" id="diet-mobile" name="mobile" inputMode="numeric" value={dietForm.mobile} onChange={handleDietChange} required placeholder="10-digit or with country code" maxLength={15} />
                    </div>
                  </div>
                  <div className="form-group">
                    <label htmlFor="diet-email">Email Address *</label>
                    <input type="email" id="diet-email" name="email" value={dietForm.email} onChange={handleDietChange} required placeholder="your@email.com" />
                  </div>
                  <div className="contact-form__row">
                    <div className="form-group">
                      <label htmlFor="diet-date"><FaCalendarCheck style={{ marginRight: '5px' }} />Preferred Date *</label>
                      <input type="date" id="diet-date" name="date" value={dietForm.date} onChange={handleDietChange} required min={getTomorrowDate()} />
                    </div>
                    <div className="form-group">
                      <label htmlFor="diet-time"><FaClock style={{ marginRight: '5px' }} />Preferred Time *</label>
                      <select id="diet-time" name="time" value={dietForm.time} onChange={handleDietChange} required>
                        <option value="">Select a time slot</option>
                        <option value="09:00 AM">09:00 AM</option>
                        <option value="10:00 AM">10:00 AM</option>
                        <option value="11:00 AM">11:00 AM</option>
                        <option value="12:00 PM">12:00 PM</option>
                        <option value="02:00 PM">02:00 PM</option>
                        <option value="03:00 PM">03:00 PM</option>
                        <option value="04:00 PM">04:00 PM</option>
                        <option value="05:00 PM">05:00 PM</option>
                        <option value="06:00 PM">06:00 PM</option>
                        <option value="07:00 PM">07:00 PM</option>
                      </select>
                    </div>
                  </div>
                  <div className="form-group">
                    <label htmlFor="diet-concern">{isPhysiotherapist ? 'Condition / Problem' : 'Health Concern / Goal'}</label>
                    <textarea
                      id="diet-concern"
                      name="concern"
                      value={dietForm.concern}
                      onChange={handleDietChange}
                      rows={3}
                      placeholder={isPhysiotherapist
                        ? 'e.g. Back pain, knee pain, posture correction, post-injury recovery...'
                        : 'e.g. Weight loss, diabetes management, sports nutrition, general wellness...'}
                    />
                  </div>
                  <button type="submit" className="btn-primary dietitian-form__submit" disabled={dietLoading}>
                    {dietLoading ? 'BOOKING...' : `BOOK ${consultationLabel.toUpperCase()} CONSULTATION`}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Contact;
